import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import {
  BrandingService,
  CompanyBranding,
  CompanyBrandingHelper,
  LiquidityEvent,
  LoanFileTemplateData,
  LoanHelper,
  NeedsAttentionModel,
  parseNumber,
  SsoAccountData,
  SsoAccountFacade,
  SsoAccountCheckoutState,
  IdentityVerificationAction,
  AgreementSigningStatus
} from "common";
import { LoanData, ProductCode } from "../../loan/domain/models/loan.model";
import { ApplicationData } from "../../application/application.model";
import { AccountTransactionService } from "../../account/account-transaction/account-transaction.service";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import { LiquidityEventFacade } from "../../liquidity-event/domain/+state/liquidity-event.facade";
import { combineLatest } from "rxjs";

@UntilDestroy()
@Component({
    selector: "ifp-sidenav-content",
    templateUrl: "./sidenav-content.component.html",
    styleUrls: ["./sidenav-content.component.scss"],
    standalone: false
})
export class SidenavContentComponent implements OnInit {
  currentLoan: LoanData;
  currentLiquidityEvent: LiquidityEvent;
  currentLoanFileTemplates: LoanFileTemplateData[];
  currentSsoAccount: SsoAccountData;
  currentApplication: ApplicationData;
  productCode = ProductCode;
  hideNavbar: boolean;
  isIdentityVerified: boolean;
  hasAvailableRenewalToSign: boolean;
  shouldDisplayLoanBarChart: boolean;
  legalEmail: string;
  layout2: boolean;
  isNPLLoan: boolean;
  colorSchema: string;
  needsAttentionModel: NeedsAttentionModel;
  branding: CompanyBranding;

  constructor(
    private route: ActivatedRoute,
    private loanFacade: LoanFacade,
    private liquidityEventFacade: LiquidityEventFacade,
    private transactionService: AccountTransactionService,
    private brandingService: BrandingService,
    private router: Router,
    private ssoAccountFacade: SsoAccountFacade,
    breakpointObserver: BreakpointObserver
  ) {
    breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .subscribe((result) => (this.layout2 = result.matches));
  }

  ngOnInit() {
    combineLatest([this.route.data, this.brandingService.getBranding$(), this.ssoAccountFacade.getSsoAccountCheckoutState$()])
    .pipe(untilDestroyed(this)).subscribe((combinedResult: Array<Data | CompanyBranding | SsoAccountCheckoutState>) => {
      const data = combinedResult[0] as Data;
      const branding = combinedResult[1] as CompanyBranding;
      const checkoutState = combinedResult[2] as SsoAccountCheckoutState;

      this.currentLoanFileTemplates = data.loanFileTemplates;
      this.currentApplication = data.application;
      this.currentSsoAccount = data.currentAccount;
      this.hideNavbar = data.sidenavDisabled;
      this.isIdentityVerified = checkoutState?.identityVerificationAction === IdentityVerificationAction.Skip;
      const accountMode = data.loans?.length > 0;
      this.initBranding(branding);

      if(data.loan){
        this.initNeedsAttentionModel(data);
      }

      if (accountMode) {
        this.initLoan();
    }
    });
  }

  private initNeedsAttentionModel(data: Data): void {
    const entityId = data.loan.entityId;
    const entity = data?.entities?.find(entity => entity.id === entityId);

    const advisorPhone = CompanyBrandingHelper.getAdvisorPhone(this.branding, data.loan?.advisorPhone);
    
    this.needsAttentionModel = {
      isApplicable: entity?.isDrawSuspended,
      messageInput: LoanHelper.getNeedsAttentionMessageInput({...data.loan, advisorPhone: advisorPhone })
    };
  }

  private initLoan() {
    this.loanFacade
      .getLoan$(parseNumber(this.route.snapshot.params.accountId))
      .pipe(untilDestroyed(this))
      .subscribe((loan) => {
        this.currentLoan = loan;
        this.getUnprocessedByLoan();
        this.isNPLLoan = this.currentLoan?.isNPLLoan;
        // Must be computed here, not in ngOnInit: the checkout state resolves asynchronously,
        // so ngOnInit runs before the loan arrives (RYNO-14861).
        this.shouldDisplayLoanBarChart = !!(this.currentLoan?.loanInfo?.principalBalance &&
          this.currentLoan?.loanInfo?.paymentAmount &&
          !this.currentLoan?.noAutoPayments);
      });

    this.liquidityEventFacade
      .getLiquidityEventForLoan$(parseNumber(this.route.snapshot.params.accountId))
      .pipe(untilDestroyed(this))
      .subscribe((liquidityEvent) => {
        this.currentLiquidityEvent = liquidityEvent;
      })
  }

  private getUnprocessedByLoan() {
    if (!this.currentLoan?.id) {
      return;
    }

    const loanWithAgreementState = LoanHelper.getLoanWithAgreementState(this.currentLoan.id, this.currentSsoAccount);

    if (loanWithAgreementState?.agreementState?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser && loanWithAgreementState?.agreementState?.isLoanVisibleForUser) {
      this.router.navigate([
        `account/${this.currentLoan.id}/setup/sign-agreement`,
      ]);
    } else {
      this.hasAvailableRenewalToSign = loanWithAgreementState?.renewalApplication?.agreementSigningStatus === AgreementSigningStatus.ReadyToSign;
    }
  }

  private initBranding(branding: CompanyBranding): void {
    this.colorSchema = branding?.colorSchema;
    this.legalEmail = branding?.legalEmail ?? "legal@ideafinancial.com";
    this.branding = branding;
  }
}
